import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Alert from "react-s-alert";
import PsychometricService from "../../services/PsychometricService";
import GeneralService from "../../services/GeneralService";
import ErrorBoundary from "../general/ErrorBoundary";
import { ThemeContext } from "../../contexts/ThemeContext";
import Papa from "papaparse"; 
import jsPDF from "jspdf";
import  html2pdf  from "html2pdf.js";
import PsychometricTestPDFLayout from "../psychometric/PsychometricTestPDFLayout";
import ReactDOMServer from "react-dom/server";
const PsychometricUsers = () => {
  // States
  const [isLoading, setIsLoading] = useState(false);
  const { theme, fontFamily, colors, fontWeight } = useContext(ThemeContext);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState({
    isVisible: false,
    selectedIndex: -1,
    userDetails: {},
  });
  const [editModal, setEditModal] = useState({
    isVisible: false,
    selectedIndex: -1,
    formData: {},
  });
  const [createModal, setCreateModal] = useState({
    isVisible: false,
    formData: {
      uniqueID: "",  // Changed from 'id' to 'uniqueID'
      firstName: "",
      lastName: "",
      gender: "",
      organization: ""
    },
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchVal, setSearchVal] = useState("");
  const [csvModal, setCsvModal] = useState({ isOpen: false });
  const [csvFile, setCsvFile] = useState(null);
  const [isCsvProcessing, setIsCsvProcessing] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);

  // Fetch users on component mount and when pagination, limit, or search value changes
  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    updateDisplayedUsers();
  }, [allUsers, pagination.page, pagination.limit, searchVal]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const data = await PsychometricService.getUsers();
      
      if (data && Array.isArray(data.users)) {
        setAllUsers(data.users);
        setPagination(prev => ({
          ...prev,
          totalResults: data.users.length,
          totalPages: Math.ceil(data.users.length / prev.limit)
        }));
      } else {
        setAllUsers([]);
        console.error("Unexpected data structure:", data);
      }

      setIsLoading(false);
    } catch (err) {
      Alert.error(
        "Some error occurred, please try again later or report to the support team.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
      GeneralService.logError(
        "at PsychometricUsers fetchUsers",
        "Error in try catch block",
        err.stack
      );
      setIsLoading(false);
    }
  };

  const updateDisplayedUsers = () => {
    let filteredUsers = allUsers;
    if (searchVal) {
      filteredUsers = allUsers.filter(user => 
        user.firstName.toLowerCase().includes(searchVal.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchVal.toLowerCase()) ||
        user.uniqueID.toLowerCase().includes(searchVal.toLowerCase())
      );
    }
    
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    setDisplayedUsers(filteredUsers.slice(startIndex, endIndex));
    
    setPagination(prev => ({
      ...prev,
      totalResults: filteredUsers.length,
      totalPages: Math.ceil(filteredUsers.length / prev.limit)
    }));
  };
  const generatePDF = async (index) => {
    try {
      const userData = displayedUsers[index];
      console.log("Generating PDF with userData:", userData);
      
      // Create a temporary div to render the PDF layout
      const element = document.createElement('div');
      element.innerHTML = ReactDOMServer.renderToString(
        <PsychometricTestPDFLayout userData={userData} />
      );
      document.body.appendChild(element);

      const opt = {
        margin: 10,
        filename: `psychometric-report-${userData.firstName}-${userData.lastName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        output: 'blob'
      };

      try {
        // Generate PDF as blob
        const pdfBlob = await html2pdf().set(opt).from(element).save().output('blob');
        
        // Create blob URL
        const blobUrl = URL.createObjectURL(pdfBlob);
        
        // Open in new tab
        window.open(blobUrl, '_blank');
        
        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        
      } catch (error) {
        console.error("Error in PDF generation:", error);
      } finally {
        // Clean up the temporary element
        document.body.removeChild(element);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      Alert.error("Failed to generate PDF report");
    }
  };

  
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchVal(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePagination = (e, index) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: index }));
  };

  // Function to show details modal
  const showDetails = (index) => {
    const userDetails = displayedUsers[index];
    console.log("User details:", userDetails);
    setModal({
      isVisible: true,
      selectedIndex: index,
      userDetails: userDetails
    });
  };

  // Function to toggle edit modal
  const toggleEditModal = (index) => {
    const user = displayedUsers[index];
    setEditModal({
      isVisible: true,
      selectedIndex: index,
      formData: {
        uniqueID: user.uniqueID, // Keep uniqueID in the form data
        firstName: user.firstName,
        lastName: user.lastName,
        gender: user.gender,
        organization: user.organization,
      },
    });
  };

  // Function to handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditModal({
      ...editModal,
      formData: {
        ...editModal.formData,
        [name]: value,
      },
    });
  };

  // Function to handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const userId = displayedUsers[editModal.selectedIndex]._id; // Use _id instead of uniqueID
    try {
      const result = await PsychometricService.updateUser(
        userId,
        editModal.formData
      );

      if (result.success) {
        // Update allUsers array in state with updated user details
        const updatedAllUsers = allUsers.map(user => 
          user._id === userId ? { ...user, ...editModal.formData } : user
        );
        setAllUsers(updatedAllUsers);
        setEditModal({ ...editModal, isVisible: false });
        Alert.success("User details updated successfully");
        updateDisplayedUsers(); // Refresh the displayed users
      } else {
        Alert.error(result.message || "Failed to update user details");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      Alert.error("Failed to update user details");
    }
  };

  // Function to handle delete user click
  const handleDeleteClick = async (index) => {
    const user = displayedUsers[index];
    console.log("Attempting to delete user:", user);
    try {
      const result = await PsychometricService.deleteCandidate(user._id);
      console.log("Delete result:", result);
      if (result.success) {
        // Update allUsers array
        const updatedAllUsers = allUsers.filter(u => u._id !== user._id);
        setAllUsers(updatedAllUsers);

        // Update displayedUsers array
        const updatedDisplayedUsers = displayedUsers.filter(u => u._id !== user._id);
        setDisplayedUsers(updatedDisplayedUsers);

        // Update pagination if necessary
        if (updatedDisplayedUsers.length === 0 && pagination.page > 1) {
          setPagination(prev => ({
            ...prev,
            page: prev.page - 1,
            totalResults: prev.totalResults - 1,
            totalPages: Math.ceil((prev.totalResults - 1) / prev.limit)
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            totalResults: prev.totalResults - 1,
            totalPages: Math.ceil((prev.totalResults - 1) / prev.limit)
          }));
        }

        Alert.success("User deleted successfully");
      } else {
        console.error("Failed to delete user:", result);
        Alert.error(result.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      if (error.response) {
        console.error("Server response:", error.response);
        Alert.error(`Failed to delete user: ${error.response.data?.message || "Unknown server error"}`);
      } else if (error.request) {
        console.error("No response received:", error.request);
        Alert.error("Failed to delete user: No response from server");
      } else {
        console.error("Error setting up request:", error.message);
        Alert.error(`Failed to delete user: ${error.message}`);
      }
    }
  };

  // Function to toggle create modal
  const toggleCreateModal = () => {
    setCreateModal({ ...createModal, isVisible: !createModal.isVisible });
  };

  // Function to handle create form changes
  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateModal(prevState => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  };

  // New function to refresh the user list
  const refreshUserList = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  // Updated handleCreateSubmit function
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const { uniqueID, firstName, lastName, gender, organization } = createModal.formData;
    try {
      const result = await PsychometricService.createCandidate(
        uniqueID, firstName, lastName, gender, organization
      );
      console.log("Create candidate result:", result);

      if (!result.candidateExists) {
        Alert.success(result.message || "User created successfully", {
          position: "top-right",
          effect: "slide",
          timeout: 3000
        });
        toggleCreateModal();
        refreshUserList();
      } else {
        Alert.error("Candidate already exists", {
          position: "top-right",
          effect: "slide",
          timeout: 3000
        });
      }
    } catch (error) {
      console.error("Error creating candidate:", error);
      Alert.error(error.message || "Failed to create candidate", {
        position: "top-right",
        effect: "slide",
        timeout: 3000
      });
    }
  };

  // Function to toggle CSV import modal
  const toggleCsvModal = () => {
    setCsvModal({ ...csvModal, isOpen: !csvModal.isOpen });
  };

  // Function to handle CSV file input change
  const handleCsvChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  // Function to handle CSV import form submission
  const handleCsvSubmit = async (e) => {
    e.preventDefault();
  
    if (!csvFile) {
      Alert.error("Please select a CSV file to upload.");
      return;
    }
  
    setIsCsvProcessing(true);
  
    try {
      // Parse the CSV file
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const users = results.data;
  
          // Batch requests
          const promises = users.map(async (user) => {
            const { id, firstName, lastName, gender, organization } = user;
            const normalizedUser = {
              id: (id || "").trim(),
              firstName: (firstName || "").trim(),
              lastName: (lastName || "").trim(),
              gender: (gender || "").trim(),
              organization: (organization || "").trim(),
            };
            try {
              const result = await PsychometricService.createCandidate(
                normalizedUser.id,
                normalizedUser.firstName,
                normalizedUser.lastName,
                normalizedUser.gender,
                normalizedUser.organization
              );
              if (result.candidateExists) {
                Alert.error(result.message.msgBody);
              } else {
                Alert.success(result.message.msgBody);
              }
            } catch (error) {
              console.error("Error creating candidate:", error);
              Alert.error(`Failed to create candidate: ${firstName} ${lastName}`);
            }
          });
  
          // Wait for all requests to complete
          await Promise.all(promises);
  
          fetchUsers();
          toggleCsvModal();
          setIsCsvProcessing(false);
        },
      });
    } catch (error) {
      console.error("Error parsing CSV file:", error);
      Alert.error("Failed to parse CSV file.");
      setIsCsvProcessing(false);
    }
  };
  

  return (
    <ErrorBoundary>
      <div
        className="psychometric-users"
        style={{ fontFamily, color: colors.text }}
      >
        <h2 style={{ fontWeight, paddingLeft: "20px" }}>
          Psychometric Candidates
        </h2>
        <div className="card mb-4">
          <div className="card-header">Candidate List</div>
          <div className="card-body py-0">
            <div
              className="mb-3 d-flex justify-content-between align-items-center"
              style={{ marginTop: "20px" }}
            >
              {/* Search bar */}
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchVal}
                onChange={handleSearch}
                style={{ width: "300px" }}
              />
              {/* Buttons */}
              <div>
                <Button
                  color="primary"
                  style={theme.formButton}
                  onClick={toggleCreateModal}
                  className="mr-2"
                >
                  Add New User
                </Button>{" "}
                <Button
                  color="secondary"
                  style={theme.formButton}
                  onClick={toggleCsvModal}
                >
                  Import Users from CSV
                </Button>
              </div>
            </div>

            {/* Table */}
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Gender</th>
                  <th>Organization</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((user, index) => (
                  <tr key={index}>
                    <td>{user.uniqueID}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.gender}</td>
                    <td>{user.organization}</td>
                    <td>
                      <Button
                        color="info"
                        style={theme.formButton}
                        onClick={() => showDetails(index)}
                        className="mr-2"
                      >
                        View Details
                      </Button>
                      <Button
                        color="primary"
                        style={theme.formButton}
                        onClick={() => toggleEditModal(index)}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        color="primary"
                        style={theme.formButton}
                        onClick={() => generatePDF(index)}
                        className="mr-2"
                      >
                        View Report
                      </Button>
                      <Button
                        color="danger"
                        style={theme.formButton}
                        onClick={() => handleDeleteClick(index)}
                      >
                        Delete
                      </Button>

                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            <Pagination aria-label="Users pagination">
              <PaginationItem disabled={pagination.page === 1}>
                <PaginationLink
                  previous
                  onClick={(e) => handlePagination(e, pagination.page - 1)}
                />
              </PaginationItem>
              {[...Array(pagination.totalPages).keys()].map((page) => (
                <PaginationItem
                  key={page}
                  active={page + 1 === pagination.page}
                >
                  <PaginationLink
                    onClick={(e) => handlePagination(e, page + 1)}
                  >
                    {page + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem
                disabled={pagination.page === pagination.totalPages}
              >
                <PaginationLink
                  next
                  onClick={(e) => handlePagination(e, pagination.page + 1)}
                />
              </PaginationItem>
            </Pagination>

            {/* Modals */}
            {/* User Details Modal */}
            <Modal
              isOpen={modal.isVisible}
              toggle={() => setModal({ ...modal, isVisible: false })}
            >
              <ModalHeader
                toggle={() => setModal({ ...modal, isVisible: false })}
              >
                User Details
              </ModalHeader>
              
              <ModalBody>
                {modal.userDetails && (
                  <>
                    <p>
                      <strong>Id:</strong> {modal.userDetails.uniqueID || 'N/A'}
                    </p>
                    <p>
                      <strong>First Name:</strong> {modal.userDetails.firstName || 'N/A'}
                    </p>
                    <p>
                      <strong>Last Name:</strong> {modal.userDetails.lastName || 'N/A'}
                    </p>
                    <p>
                      <strong>Gender:</strong> {modal.userDetails.gender || 'N/A'}
                    </p>
                    <p>
                      <strong>Organization:</strong> {modal.userDetails.organization || 'N/A'}
                    </p>
                  </>
                )}
              </ModalBody>
            </Modal>

            {/* Edit User Modal */}
            <Modal
              isOpen={editModal.isVisible}
              toggle={() => setEditModal({ ...editModal, isVisible: false })}
            >
              <ModalHeader
                toggle={() => setEditModal({ ...editModal, isVisible: false })}
              >
                Edit User
              </ModalHeader>
              <ModalBody>
                <form onSubmit={handleEditSubmit}>
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      value={editModal.formData.firstName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={editModal.formData.lastName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <input
                      type="text"
                      className="form-control"
                      name="gender"
                      value={editModal.formData.gender}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization</label>
                    <input
                      type="text"
                      className="form-control"
                      name="organization"
                      value={editModal.formData.organization}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    style={theme.formButton}
                    color="primary"
                  >
                    Update User
                  </Button>{" "}
                  <Button
                    color="secondary"
                    style={theme.formButton}
                    onClick={() =>
                      setEditModal({ ...editModal, isVisible: false })
                    }
                  >
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>

            {/* Create User Modal */}
            <Modal isOpen={createModal.isVisible} toggle={toggleCreateModal}>
              <ModalHeader toggle={toggleCreateModal}>Add New User</ModalHeader>
              <ModalBody>
                <form onSubmit={handleCreateSubmit}>
                  <div className="form-group">
                    <label>Unique ID</label>
                    <input
                      type="text"
                      className="form-control"
                      name="uniqueID"
                      value={createModal.formData.uniqueID}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      value={createModal.formData.firstName}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={createModal.formData.lastName}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <input
                      type="text"
                      className="form-control"
                      name="gender"
                      value={createModal.formData.gender}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization</label>
                    <input
                      type="text"
                      className="form-control"
                      name="organization"
                      value={createModal.formData.organization}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    style={theme.formButton}
                    color="primary"
                  >
                    Add User
                  </Button>{" "}
                  <Button
                    color="secondary"
                    style={theme.formButton}
                    onClick={toggleCreateModal}
                  >
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>

            {/* CSV Import Modal */}
            <Modal isOpen={csvModal.isOpen} toggle={toggleCsvModal}>
              <ModalHeader toggle={toggleCsvModal}>
                Import Users from CSV
              </ModalHeader>
              <ModalBody>
                <p>Upload a CSV file formatted as:</p>
                <pre>id,first name, last name,gender,organization</pre>
                <form onSubmit={handleCsvSubmit}>
                  <div className="form-group">
                    <input
                      type="file"
                      onChange={handleCsvChange}
                      accept=".csv"
                    />
                  </div>
                  <Button
                    type="submit"
                    style={theme.formButton}
                    color="primary"
                    disabled={isCsvProcessing}
                  >
                    {isCsvProcessing ? "Processing..." : "Import CSV"}
                  </Button>{" "}
                  <Button
                    color="secondary"
                    style={theme.formButton}
                    onClick={toggleCsvModal}
                  >
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default PsychometricUsers;
